import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useContext, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import Header from "@/components/header/Header";
import { Button } from "@/components/ui/button";
import ButtonContainer from "@/components/button-container";
import CredentialsForm from "@/components/Form/Form";
import SimpleErrorMessage from "@/components/error-message/new-error-message";

import { genericService } from "@/services/GenericService";

import { AuthFlowType, FLOW_CONFIGS, FlowConfig } from "@/config/ConsentsDeclineConfirmationFlowConfig";

import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";

import { FormError } from "@/models/FormError";
import { ConsentDeclineConfirmationRequest } from "@/models/shared/consents/ConsentAuthorizationRequest";
import { ConsentsResponse } from "@/models/continente-credentials/mobile/ConsentsResponse";

import { tryCatch } from "@/utils/try-catch";
import { resolveClientTopLogo } from "@/utils/clientBranding";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";

interface SharedConsentDeclineConfirmationProps {
  flowConfig: FlowConfig;
}

type Form = ConsentDeclineConfirmationRequest & FormError;

export default function SharedConsentDeclineConfirmationScreen({ flowConfig }: SharedConsentDeclineConfirmationProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleResponse } = useApiResponseHandler();
  const [loading, setLoading] = useState(false);
  const location = useLocation<ConsentsResponse>();
  const digitalAssetContext = useContext(DigitalAssetContext);

  const properties = location.state?.properties || {};
  const nextStep = location.state?.nextStep ?? "api" + location.pathname;

  const { clientName } = properties;

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const onGoBack = () => {
    history.goBack();
  };

  const topLogo = useMemo(
    () => resolveClientTopLogo(digitalAssetContext.digitalAsset.clientLogo),
    []
  );

  const onSubmit: SubmitHandler<ConsentDeclineConfirmationRequest> = async (data) => {
    setLoading(true);
    const { data: response, error } = await tryCatch(genericService.post<ConsentsResponse, ConsentDeclineConfirmationRequest>(
      nextStep,
      {
        confirmDecline: data.confirmDecline,
      },
    ));

    handleResponse(response, {
      setError
    }, error);

    setLoading(false);
  };

  const handleChooseOption = (confirmDecline: boolean) => {
    setValue("confirmDecline", confirmDecline);
    clearErrors();
    handleSubmit(onSubmit)();
  };

  return (
    <CredentialsForm
      id={flowConfig.form}
      form={formState}
      autoComplete="off"
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Header
        title={t(flowConfig.labels.headerTitle)}
        showLoading={loading}
        showBackButton={false}
        topLogo={topLogo}
      />
      <div>
        <span>
          {t("consent.decline_confirmation.subtitle.1", { clientName: clientName })}
          <a className="font-bold underline text-[var(--on-surface-primary-default-variant)] hover:text-[var(--on-surface-primary-hover-variant)] pressed:text-[var(--on-surface-primary-pressed-variant)] disabled:text-[var(--on-surface-variant-2)]">{t("consent.decline_confirmation.subtitle.2")}</a>
          {t("consent.decline_confirmation.subtitle.3")}
        </span>
      </div>

      {errors.submitError && <SimpleErrorMessage error={errors.submitError.message} />}
      <ButtonContainer className="mt-8 pb-8">
        <Button
          type="button"
          disabled={loading}
          onClick={() => handleChooseOption(true)}
          aria-label={t("consent.decline_confirmation.continue_aria")}
        >
          {t("consent.decline_confirmation.continue")}
        </Button>
        <Button
          variant="secondary"
          type="button"
          disabled={loading}
          onClick={() => handleChooseOption(false)}
          aria-label={t("consent.decline_disabled_aria")}
        >
          {t("consent.decline_confirmation.i_want_to_share")}
        </Button>
      </ButtonContainer>
    </CredentialsForm>
  )
}


export const createConsentDeclineConfirmationPage = (flowConfig: AuthFlowType) => {
  const config = FLOW_CONFIGS[flowConfig];
  return () => <SharedConsentDeclineConfirmationScreen flowConfig={config} />;
};

export const ConsentDeclineConfirmation = createConsentDeclineConfirmationPage(AuthFlowType.CREDENTIALS);
export const SCConsentDeclineConfirmation = createConsentDeclineConfirmationPage(AuthFlowType.SITE_CREDENTIALS);